import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { H1Title, Paragraph } from "../components/typography/typography";
import { AppButton } from "../components/buttons/button";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt, faUsers, faClock, faCheckCircle, faEdit, faPaperPlane, faTrophy } from "@fortawesome/free-solid-svg-icons";
import CompetitionService from "../services/competitionService";
import SubmissionService from "../services/submissionService";
import TeamService from "../services/teamService";
import confetti from "canvas-confetti";

const STATUS_DISPLAY = {
  draft: { label: 'Draft', icon: faEdit, color: 'text-orange-500', bg: 'bg-orange-500/10' },
  submitted: { label: 'Submitted', icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light' },
  under_review: { label: 'Under Review', icon: faClock, color: 'text-primary', bg: 'bg-primary-bg' },
  advanced: { label: 'Advanced', icon: faCheckCircle, color: 'text-green', bg: 'bg-green-light' },
  eliminated: { label: 'Eliminated', icon: faClock, color: 'text-red-500', bg: 'bg-red-500/10' },
};

export default function Dashboard() {
  const user = useSelector((state) => state.auth.user);
  const [competition, setCompetition] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [team, setTeam] = useState(null);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      const { data: compData } = await CompetitionService.getActive();
      const comp = compData.data.competition;
      setCompetition(comp);

      const [subRes, teamRes, invitesRes] = await Promise.all([
        SubmissionService.getMySubmission(comp._id).catch(() => null),
        TeamService.getMyTeam().catch(() => null),
        TeamService.getInvites().catch(() => null),
      ]);

      if (subRes?.data?.data?.submission) setSubmission(subRes.data.data.submission);
      if (teamRes?.data?.data?.team) setTeam(teamRes.data.data.team);
      if (invitesRes?.data?.data?.invites) setInvites(invitesRes.data.data.invites);
    } catch { /* no active competition */ }
    finally { setLoading(false); }
  };

  const subStatus = submission ? STATUS_DISPLAY[submission.status] || STATUS_DISPLAY.draft : null;
  const currentRoundNum = competition?.currentRound || 1;
  const isWinner = submission?.isWinner === true;

  const getSubmissionCTA = () => {
    if (!submission) return { label: 'Start Proposal', to: '/submission/new', variant: 'primary' };
    if (submission.status === 'draft') return { label: 'Continue Draft', to: '/submission/new', variant: 'primary' };
    switch (submission.currentRound) {
      case 'round2':
        return submission.videoPitch?.url
          ? { label: 'Update Video Pitch', to: '/video-pitch', variant: 'outline' }
          : { label: 'Submit Video Pitch', to: '/video-pitch', variant: 'primary' };
      case 'round3':
        return { label: 'Awaiting Interview', to: '/dashboard', variant: 'outline', subtitle: 'Round 3 finalist interviews are scheduled via email — no action needed on the portal.' };
      case 'round4':
        return { label: 'Awaiting Team Meeting', to: '/dashboard', variant: 'outline', subtitle: 'Round 4 "Meet the Team" details will be emailed separately.' };
      case 'round5':
        return { label: 'Track Fundraising', to: '/fundraising', variant: 'primary' };
      case 'round6':
      case 'implementation':
        return { label: 'Implementation', to: '/implementation', variant: 'primary' };
      default:
        return { label: 'View Submission', to: '/submission/new', variant: 'outline' };
    }
  };
  const submissionCTA = getSubmissionCTA();

  // Fire confetti for winners
  useEffect(() => {
    if (isWinner) {
      const duration = 3000;
      const end = Date.now() + duration;
      const frame = () => {
        confetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0 } });
        confetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1 } });
        if (Date.now() < end) requestAnimationFrame(frame);
      };
      frame();
    }
  }, [isWinner]);

  return (
    <div className="space-y-6">
      {/* Winner Banner */}
      {isWinner && (
        <div className="bg-gradient-to-r from-yellow-500 to-orange-500 rounded-xl p-6 md:p-8 text-center">
          <FontAwesomeIcon icon={faTrophy} className="text-white text-4xl mb-3" />
          <H1Title size="md" className="!text-white">
            Congratulations, {user?.firstName}!
          </H1Title>
          <Paragraph size="base" className="text-white/90 mt-2">
            Your team has been selected as a winner of The Human Development Lab!
          </Paragraph>
        </div>
      )}

      {/* Welcome */}
      <div className={`bg-gradient-to-r ${isWinner ? 'from-yellow-600 to-orange-500' : 'from-primary to-primary-light'} rounded-xl p-6 md:p-8 text-white`}>
        <H1Title size="sm" className="!text-white">
          Welcome back, {user?.firstName}!
        </H1Title>
        <Paragraph size="base" className="text-white/80 mt-2">
          The Human Development Lab - Competition Portal
        </Paragraph>
      </div>

      {/* Status Cards */}
      <div className="grid md:grid-cols-3 gap-4">
        {/* Current Round */}
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary-bg rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faClock} className="text-primary" />
            </div>
            <div>
              <p className="text-sm text-grey-667">Current Round</p>
              <p className="font-bold">
                {competition ? `Round ${currentRoundNum} - ${competition.rounds?.find(r => r.roundNumber === currentRoundNum)?.name || 'Active'}` : 'Loading...'}
              </p>
            </div>
          </div>
          <p className="text-xs text-grey-98">{competition?.status === 'registration_open' ? 'Submissions are open' : competition?.status || ''}</p>
        </div>

        {/* My Submission */}
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${subStatus?.bg || 'bg-grey-200'}`}>
              <FontAwesomeIcon icon={subStatus?.icon || faFileAlt} className={subStatus?.color || 'text-grey-667'} />
            </div>
            <div>
              <p className="text-sm text-grey-667">My Submission</p>
              <p className="font-bold">{subStatus?.label || 'Not started'}</p>
            </div>
          </div>
          {submission?.currentRound && (
            <p className="text-xs text-grey-98 mb-2">Round: {submission.currentRound}</p>
          )}
          {submissionCTA.subtitle && (
            <p className="text-xs text-grey-667 mb-2">{submissionCTA.subtitle}</p>
          )}
          <Link to={submissionCTA.to}>
            <AppButton size="sm" fullWidth={false} className="mt-1" variant={submissionCTA.variant}>
              {submissionCTA.label}
            </AppButton>
          </Link>
        </div>

        {/* My Team */}
        <div className="bg-white rounded-xl border border-grey-200 p-5">
          <div className="flex items-center gap-3 mb-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${team ? 'bg-green-light' : invites.length > 0 ? 'bg-primary-bg' : 'bg-orange-500/10'}`}>
              <FontAwesomeIcon icon={faUsers} className={team ? 'text-green' : invites.length > 0 ? 'text-primary' : 'text-orange-500'} />
            </div>
            <div>
              <p className="text-sm text-grey-667">My Team</p>
              <p className="font-bold">
                {team?.name || (invites.length > 0 ? `${invites.length} pending invite${invites.length === 1 ? '' : 's'}` : 'No team yet')}
              </p>
            </div>
          </div>
          {team && (
            <p className="text-xs text-grey-98 mb-2">{team.members?.filter(m => m.status === 'accepted').length || 0} members</p>
          )}
          {!team && invites.length > 0 && (
            <p className="text-xs text-grey-667 mb-2">
              {invites[0].leaderId?.firstName} {invites[0].leaderId?.lastName} invited you to "{invites[0].name}"
            </p>
          )}
          <Link to="/team">
            <AppButton size="sm" variant={!team && invites.length > 0 ? 'primary' : 'outline'} fullWidth={false} className="mt-1">
              {team ? 'Manage Team' : invites.length > 0 ? 'Review Invite' : 'Create Team'}
            </AppButton>
          </Link>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-grey-200 p-6">
        <h3 className="font-bold text-lg mb-4">Competition Timeline</h3>
        <div className="space-y-4">
          {(competition?.rounds || [
            { roundNumber: 1, name: 'Proposal Submission' },
            { roundNumber: 2, name: 'Recorded Pitch' },
            { roundNumber: 3, name: 'Finalist Interviews' },
            { roundNumber: 4, name: 'Meet the Team' },
            { roundNumber: 5, name: 'Fundraising & Logistics' },
            { roundNumber: 6, name: 'Implementation' },
          ]).map((r) => {
            const isActive = r.roundNumber === currentRoundNum;
            const isCompleted = r.roundNumber < currentRoundNum;
            const dates = ['May 2026', 'August 2026', 'September 2026', 'October 2026', 'Oct-Dec 2026', 'Jan-Jun 2027'];
            return (
              <div key={r.roundNumber} className={`flex items-center gap-4 p-3 rounded-lg ${isActive ? 'bg-primary-bg border border-primary/20' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                  isActive ? 'bg-primary text-white' : isCompleted ? 'bg-green text-white' : 'bg-grey-200 text-grey-667'
                }`}>
                  {isCompleted ? <FontAwesomeIcon icon={faCheckCircle} /> : r.roundNumber}
                </div>
                <div className="flex-1">
                  <p className={`text-sm font-semibold ${isActive ? 'text-primary' : isCompleted ? 'text-green' : 'text-grey-dark'}`}>
                    Round {r.roundNumber}: {r.name}
                  </p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-full ${
                  r.status === 'active' ? 'bg-primary-bg text-primary' :
                  r.status === 'completed' ? 'bg-green-light text-green' :
                  'text-grey-98'
                }`}>
                  {r.status === 'active' ? 'Active' : r.status === 'completed' ? 'Done' : dates[r.roundNumber - 1] || ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Competition Documents */}
      {competition?.documents && Object.values(competition.documents).some(Boolean) && (
        <div className="bg-white rounded-xl border border-grey-200 p-6">
          <h3 className="font-bold text-lg mb-4">Competition Documents</h3>
          <div className="grid md:grid-cols-3 gap-4">
            {competition.documents.competitionDescription && (
              <a href={competition.documents.competitionDescription} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 border border-grey-200 rounded-lg hover:border-primary hover:bg-primary-bg/30 transition-colors">
                <FontAwesomeIcon icon={faFileAlt} className="text-primary text-lg" />
                <div>
                  <p className="text-sm font-semibold">Competition Description</p>
                  <p className="text-xs text-grey-98">Download PDF</p>
                </div>
              </a>
            )}
            {competition.documents.judgingRubric && (
              <a href={competition.documents.judgingRubric} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 border border-grey-200 rounded-lg hover:border-primary hover:bg-primary-bg/30 transition-colors">
                <FontAwesomeIcon icon={faFileAlt} className="text-primary text-lg" />
                <div>
                  <p className="text-sm font-semibold">Judging Rubric</p>
                  <p className="text-xs text-grey-98">Download PDF</p>
                </div>
              </a>
            )}
            {competition.documents.locationBrief && (
              <a href={competition.documents.locationBrief} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-4 border border-grey-200 rounded-lg hover:border-primary hover:bg-primary-bg/30 transition-colors">
                <FontAwesomeIcon icon={faFileAlt} className="text-primary text-lg" />
                <div>
                  <p className="text-sm font-semibold">Location Brief</p>
                  <p className="text-xs text-grey-98">Download PDF</p>
                </div>
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
